import React, { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { EMessageChannel } from 'src/services/api';
import { LeadMessagesModeContext } from 'src/pages/landlord/leads/messages/context/mode/context';
import { useConversationDrawer } from 'src/pages/landlord/conversations/components/drawer/use-conversation-drawer';
import { analyticsClient, AnalyticsEvent } from 'src/services/analytics';

type Props = {
    children: React.ReactNode;
};

/**
 * Drawer counterpart to `PageLeadMessagesModeProvider`: the conversation stays on the current
 * route, so prev/next navigation and close just rewrite this route's search params instead of
 * navigating to `/landlord/inner/messages/from-lead`.
 */
export default function DrawerLeadMessagesModeProvider({ children }: Props) {
    const [, setSearchParams] = useSearchParams();
    const { close } = useConversationDrawer();

    const goToConversation = useCallback((leadUuid: string, channel: EMessageChannel) => {
        analyticsClient.track(AnalyticsEvent.CONVERSATION_OPENED, {
            source: 'prev_next',
            variant: 'drawer',
            lead_uuid: leadUuid,
            channel,
        });
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set('lead_uuid', leadUuid);
            newSearchParams.set('channel', channel);
            newSearchParams.set('show_navigation', 'true');
            return newSearchParams;
        }, { replace: true });
    }, [setSearchParams]);

    return (
        <LeadMessagesModeContext.Provider value={{
            variant: 'drawer',
            goToConversation,
            close,
            backLabel: 'Close',
            escHotkeyLabel: 'Close',
            backHref: undefined,
        }}
        >
            {children}
        </LeadMessagesModeContext.Provider>
    );
}
